import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Copy, ChevronDown, FileText, DollarSign, Eye, Receipt, User, FolderOpen, Trash2, RefreshCw, X, Crown, Send, Calculator, Settings2, ArrowLeft, MoreVertical, CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import ActionBar from '@/components/shared/ActionBar';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import { supabase } from '@/lib/supabase';
import { getUnitLabel } from '@/types/unit-types';
import { useNotify } from '@/hooks/useNotify';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import DetailPageHeader from '@/components/layout/DetailPageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/utils';
import { getDisplayDescription } from '@/utils/lineItemDisplayHelpers';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import VersionStatusManager from '@/components/estimates/VersionStatusManager';
// SEO: Basic tags for this page are handled at the app level
import EstimatePreviewModal from '@/components/estimates/EstimatePreviewModal';
import DeleteConfirmationDialog from '@/components/shared/DeleteConfirmationDialog';
import VersionDeletionDialog from '@/components/estimates/VersionDeletionDialog';
import { getFinalLineItemAmount, getFinalLineItemUnitCost } from '@/utils/estimateExportUtils';
import CostAnalysisModal from '@/components/estimates/CostAnalysisModal';
// Calculator and Settings2 icons are imported above
import { calculateAdjustedPrices } from '@/utils/adjustmentCalculations';
import { AdjustmentSettings } from '@/types/adjustment-filters';
import DraftVersionBanner from '@/components/estimates/DraftVersionBanner';
import VersionSummary from '@/components/estimates/components/VersionSummary';
import EstimatePricingRefreshButton from '@/components/estimates/EstimatePricingRefreshButton';
import SendEstimateDialog from '@/components/estimates/SendEstimateDialog';
import DocumentSendHistory from '@/components/shared/DocumentSendHistory';



const EstimateViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPreview, setShowPreview] = useState(false);
  const [showCostAnalysis, setShowCostAnalysis] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendVersionId, setSendVersionId] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    estimateName: ''
  });
  const [versionDeleteDialog, setVersionDeleteDialog] = useState({
    isOpen: false,
    versionId: '',
    versionType: 'estimate' as 'estimate' | 'change_order',
    versionNumber: 0,
    versionStatus: 'draft'
  });
  const [adjustedLineItems, setAdjustedLineItems] = useState<Map<string, any>>(new Map());
  const [adjustedDraftTotals, setAdjustedDraftTotals] = useState<Map<string, number>>(new Map());
  const [currentViewVersion, setCurrentViewVersion] = useState<string | null>(null);
  const [showVersionChanges, setShowVersionChanges] = useState(false);

  const [estimateDateOpen, setEstimateDateOpen] = useState(false);
  const queryClient = useQueryClient();

  // Handle URL query param for auto-opening send dialog (from post-creation flow)
  useEffect(() => {
    const sendVersionParam = searchParams.get('sendVersion');
    if (sendVersionParam) {
      setSendVersionId(sendVersionParam);
      setShowSendDialog(true);
      // Clean up URL
      searchParams.delete('sendVersion');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  const {
    data: estimate,
    isLoading: isLoadingEstimate,
    error: estimateError,
    refetch
  } = useQuery({
    queryKey: ['estimate', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          *,
          customers(name, last_name)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('[EstimateViewPage] Error loading estimate:', error);
        throw error;
      }
      return data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes - reduce unnecessary refetches
  });

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: async () => {
      if (!estimate?.company_id) return null;

      const { data: companyData, error } = await supabase
        .from('companies')
        .select('is_vat_registered, "VAT Rate"')
        .eq('id', estimate.company_id)
        .single();

      if (error) throw error;
      return companyData;
    },
    enabled: !!estimate?.company_id,
    staleTime: 1000 * 60 * 10, // 10 minutes - company data rarely changes
  });

  const { data: linkedProject } = useQuery({
    queryKey: ['estimateProject', estimate?.id],
    queryFn: async () => {
      if (!estimate?.id) return null;
      
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, status, expected_start_date, expected_end_date, created_at')
        .eq('estimate_id', estimate.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching linked project:', error);
      }
      
      return data;
    },
    enabled: !!estimate?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const { data: lineItems, isLoading: isLoadingLineItems } = useQuery({
    queryKey: ['estimateLineItems', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('estimate_line_items')
        .select('*')
        .eq('estimate_id', id)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: estimateVersions, refetch: refetchVersions } = useQuery({
    queryKey: ['estimateVersions', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('estimate_versions')
        .select('*')
        .eq('estimate_id', id)
        .order('version_number', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
    staleTime: 1000 * 30, // 30 seconds - versions change more frequently
  });

  const draftVersions = estimateVersions?.filter(v => v.status === 'draft') || [];

  // Calculate adjusted totals for draft versions (applying markup removal + discount)
  useEffect(() => {
    const computeDraftTotals = async () => {
      if (!draftVersions.length || !estimate?.adjustment_metadata) {
        setAdjustedDraftTotals(new Map());
        return;
      }

      const adjustmentSettings = estimate.adjustment_metadata as unknown as AdjustmentSettings;
      if (!adjustmentSettings.filters?.length && !adjustmentSettings.discountPercentage) {
        setAdjustedDraftTotals(new Map());
        return;
      }

      const totalsMap = new Map<string, number>();

      for (const version of draftVersions) {
        const snapshot = version.line_items_snapshot;
        if (!Array.isArray(snapshot) || snapshot.length === 0) continue;

        try {
          // Cast snapshot items to match calculateAdjustedPrices input
          const snapshotItems = (snapshot as any[]).map((item: any) => ({
            id: item.id,
            component_type: item.component_type,
            component_id: item.component_id,
            quantity_in_base_units: item.quantity_in_base_units ?? item.quantity ?? 0,
            unit_cost_snapshot: item.unit_cost_snapshot ?? item.unit_cost ?? 0,
            total_cost_snapshot: item.total_cost_snapshot ?? item.total_cost ?? 0,
          }));

          const adjustedMap = await calculateAdjustedPrices(snapshotItems, adjustmentSettings);

          // Sum adjusted totals using same logic as the page
          const lineItemsTotal = (snapshot as any[]).reduce((sum: number, item: any) => {
            const adjusted = adjustedMap.get(item.id);
            const itemWithAdjustment = adjusted
              ? { ...item, _adjustedTotalCost: adjusted.adjustedTotalCost }
              : item;
            const amount = getFinalLineItemAmount(itemWithAdjustment);
            return sum + (typeof amount === 'number' && !isNaN(amount) ? amount : 0);
          }, 0);

          // Apply discount
          let finalTotal = lineItemsTotal;
          if (adjustmentSettings.discountPercentage && adjustmentSettings.discountPercentage > 0) {
            finalTotal = lineItemsTotal * (1 - adjustmentSettings.discountPercentage / 100);
          }

          totalsMap.set(version.id, finalTotal);
        } catch (error) {
          console.error('[EstimateViewPage] Error computing draft version adjusted total:', error);
        }
      }

      setAdjustedDraftTotals(totalsMap);
    };

    computeDraftTotals();
  }, [draftVersions.map(v => v.id).join(','), estimate?.adjustment_metadata]);

  // Phase 2: Fix Status Display - Use database truth with proper fallback
  const mainVersion = React.useMemo(() => {
    if (!estimateVersions || estimateVersions.length === 0) return null;
    // Prefer the estimate.main_version_id when present
    if (estimate?.main_version_id) {
      const byId = estimateVersions.find(v => v.id === estimate.main_version_id);
      if (byId) return byId;
    }
    // Fallback to main version logic based on status priority
    const byFlag = estimateVersions.find(v => v.id === estimate?.main_version_id);
    if (byFlag) return byFlag;
    // Final fallback: compute by status priority and highest version number
    const rank = (s: string) => (s === 'approved' ? 1 : s === 'sent' ? 2 : s === 'draft' ? 3 : 4);
    return [...estimateVersions].sort((a, b) => {
      const r = rank(a.status) - rank(b.status);
      if (r !== 0) return r;
      return b.version_number - a.version_number;
    })[0] || null;
  }, [estimate?.main_version_id, estimateVersions]);

  const viewingVersion = currentViewVersion ? 
    estimateVersions?.find(v => v.id === currentViewVersion) : mainVersion;

  // Phase 3: Add comprehensive null safety for line items calculation
  console.log('[EstimateViewPage] PHASE 2&3 - Version state after DB fix:', {
    estimateId: id,
    estimateStatus: estimate?.status,
    estimateVersionsCount: estimateVersions?.length || 0,
    mainVersionFromDB: mainVersion ? {
      id: mainVersion.id,
      status: mainVersion.status,
      version_number: mainVersion.version_number,
      is_main: mainVersion.id === estimate?.main_version_id
    } : null,
    viewingVersionSelected: viewingVersion ? {
      id: viewingVersion.id,
      status: viewingVersion.status,
      version_number: viewingVersion.version_number,
      is_main: viewingVersion.id === estimate?.main_version_id
    } : null,
    hasCurrentViewVersion: !!currentViewVersion
  });

  // Calculate adjusted line items when estimate or line items change
  useEffect(() => {
    const applyAdjustments = async () => {
      if (!estimate?.adjustment_metadata) {
        setAdjustedLineItems(new Map());
        return;
      }

      const adjustmentSettings = estimate.adjustment_metadata as unknown as AdjustmentSettings;
      if (!adjustmentSettings.filters?.length && !adjustmentSettings.discountPercentage) {
        setAdjustedLineItems(new Map());
        return;
      }

      // Get current line items for adjustment calculation
      const currentItems = lineItems || [];
      if (currentItems.length === 0) {
        return;
      }

      try {
        const adjustedMap = await calculateAdjustedPrices(currentItems, adjustmentSettings);
        setAdjustedLineItems(adjustedMap);
      } catch (error) {
        console.error('[EstimateViewPage] Error calculating adjustments:', error);
      }
    };

    applyAdjustments();
  }, [estimate?.adjustment_metadata, lineItems]);

  const { displayLineItems, subtotal, discountAmount, discountPercentage, taxAmount, totalAmount } = React.useMemo(() => {
    let items: any[] = [];
    
    // Phase 3: Comprehensive null safety and data source validation
    console.log('[EstimateViewPage] PHASE 3 - Line items calculation with null safety:', {
      hasViewingVersion: !!viewingVersion,
      viewingVersionId: viewingVersion?.id,
      viewingVersionStatus: viewingVersion?.status,
      hasSnapshot: !!(viewingVersion?.line_items_snapshot && Array.isArray(viewingVersion.line_items_snapshot)),
      snapshotLength: Array.isArray(viewingVersion?.line_items_snapshot) ? viewingVersion.line_items_snapshot.length : 0,
      hasLineItems: !!lineItems,
      lineItemsLength: lineItems?.length || 0,
      dataLoadingState: {
        estimateLoading: isLoadingEstimate,
        lineItemsLoading: isLoadingLineItems,
        versionsPresent: !!estimateVersions,
        mainVersionPresent: !!mainVersion
      }
    });
    
    // Prioritize version snapshot if available and valid
    if (viewingVersion?.line_items_snapshot && 
        Array.isArray(viewingVersion.line_items_snapshot) && 
        viewingVersion.line_items_snapshot.length > 0) {
      // Merge manual_description from live data since snapshots may not have it
      items = viewingVersion.line_items_snapshot.map((snapshotItem: any) => {
        const liveItem = lineItems?.find(li => li.id === snapshotItem.id);
        return {
          ...snapshotItem,
          // Merge manual_description from live data if snapshot doesn't have it
          manual_description: snapshotItem.manual_description ?? liveItem?.manual_description
        };
      });
      console.log('[EstimateViewPage] PHASE 3 - Using version snapshot with manual_description merged, sample item:', 
        items[0] ? {
          description: items[0].description,
          manual_description: items[0].manual_description,
          quantity: items[0].quantity || items[0].quantity_in_base_units,
          unit_cost: items[0].unit_cost || items[0].unit_cost_snapshot,
          total_cost: items[0].total_cost || items[0].total_cost_snapshot
        } : 'No items in snapshot');
    }
    // Fallback to live line items with proper transformation and null safety
    else if (lineItems && Array.isArray(lineItems) && lineItems.length > 0) {
      items = lineItems.map(item => {
        // Ensure we have valid numeric values, default to 0 if null/undefined
        const quantity = typeof item.quantity_in_base_units === 'number' ? item.quantity_in_base_units : 0;
        const unitCost = item.manual_override && typeof item.manual_unit_cost === 'number' 
          ? item.manual_unit_cost 
          : (typeof item.unit_cost_snapshot === 'number' ? item.unit_cost_snapshot : 0);
        const totalCost = item.manual_override && typeof item.manual_total_cost === 'number' 
          ? item.manual_total_cost 
          : (typeof item.total_cost_snapshot === 'number' ? item.total_cost_snapshot : 0);
          
        return {
          id: item.id,
          description: item.description || 'No description',
          manual_description: item.manual_description,
          quantity: quantity,
          quantity_in_base_units: quantity,
          unit_cost: unitCost,
          total_cost: totalCost,
          unit_cost_snapshot: typeof item.unit_cost_snapshot === 'number' ? item.unit_cost_snapshot : 0,
          total_cost_snapshot: typeof item.total_cost_snapshot === 'number' ? item.total_cost_snapshot : 0,
          manual_unit_cost: item.manual_unit_cost,
          manual_total_cost: item.manual_total_cost,
          base_unit: item.base_unit,
          manual_override: !!item.manual_override
        };
      });
      console.log('[EstimateViewPage] PHASE 3 - Using live line items, sample transformed item:', 
        items[0] ? {
          description: items[0].description,
          quantity: items[0].quantity,
          unit_cost: items[0].unit_cost,
          total_cost: items[0].total_cost
        } : 'No items after transformation');
    }
    
    console.log('[EstimateViewPage] PHASE 3 - Final items for calculation:', items.length, 'items');
    
    // Apply adjustments to line items for display
    let itemsWithAdjustments = items;
    if (adjustedLineItems.size > 0) {
      itemsWithAdjustments = items.map(item => {
        const adjusted = adjustedLineItems.get(item.id);
        if (adjusted) {
          return {
            ...item,
            _adjustedUnitCost: adjusted.adjustedUnitCost,
            _adjustedTotalCost: adjusted.adjustedTotalCost,
            _originalUnitCost: item.unit_cost_snapshot || item.unit_cost,
            _originalTotalCost: item.total_cost_snapshot || item.total_cost
          };
        }
        return item;
      });
    }
    
    const lineItemsTotal = itemsWithAdjustments.reduce((sum: number, item: any) => {
      // Phase 3: Add null safety to line item amount calculation
      const itemAmount = getFinalLineItemAmount(item);
      const safeAmount = typeof itemAmount === 'number' && !isNaN(itemAmount) ? itemAmount : 0;
      
      console.log('[EstimateViewPage] PHASE 3 - Item calculation with null safety:', {
        description: item.description,
        quantity: item.quantity || item.quantity_in_base_units,
        unitCost: item.unit_cost || item.unit_cost_snapshot,
        totalCost: item.total_cost || item.total_cost_snapshot,
        rawAmount: itemAmount,
        safeAmount: safeAmount,
        runningSum: sum + safeAmount
      });
      return sum + safeAmount;
    }, 0);

    console.log('[EstimateViewPage] PHASE 3 - Line items total calculated:', lineItemsTotal);

    let calculatedSubtotal = lineItemsTotal;
    
    // Calculate discount amount AFTER subtotal
    const adjustmentSettings = estimate?.adjustment_metadata as unknown as AdjustmentSettings | undefined;
    let calculatedDiscountAmount = 0;
    let calculatedDiscountPercentage = 0;
    
    if (adjustmentSettings && adjustmentSettings.discountPercentage) {
      calculatedDiscountPercentage = adjustmentSettings.discountPercentage;
      calculatedDiscountAmount = lineItemsTotal * (calculatedDiscountPercentage / 100);
    }
    
    // Apply discount before VAT
    const subtotalAfterDiscount = lineItemsTotal - calculatedDiscountAmount;
    
    let calculatedTaxAmount = 0;
    let calculatedTotal = subtotalAfterDiscount;

    if (estimate && company !== undefined) {
      const companyVatRate = typeof company?.["VAT Rate"] === 'number' ? company["VAT Rate"] : 15;
      
      if (company?.is_vat_registered && companyVatRate > 0) {
        if (estimate.tax_inclusive) {
          calculatedTaxAmount = (subtotalAfterDiscount * companyVatRate) / (100 + companyVatRate);
          calculatedTotal = subtotalAfterDiscount;
        } else {
          calculatedTaxAmount = (subtotalAfterDiscount * companyVatRate) / 100;
          calculatedTotal = subtotalAfterDiscount + calculatedTaxAmount;
        }
      }
    }

    console.log('[EstimateViewPage] Final calculations:', {
      subtotal: calculatedSubtotal,
      discountAmount: calculatedDiscountAmount,
      discountPercentage: calculatedDiscountPercentage,
      taxAmount: calculatedTaxAmount,
      totalAmount: calculatedTotal
    });

    return {
      displayLineItems: itemsWithAdjustments,
      subtotal: calculatedSubtotal,
      discountAmount: calculatedDiscountAmount,
      discountPercentage: calculatedDiscountPercentage,
      taxAmount: calculatedTaxAmount,
      totalAmount: calculatedTotal
    };
  }, [viewingVersion?.line_items_snapshot, lineItems, estimate, company, adjustedLineItems]);

  const { data: invoices } = useQuery({
    queryKey: ['estimateInvoices', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, status, total_amount, created_at')
        .eq('estimate_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const deleteEstimateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No estimate ID');
      
      const { data, error } = await supabase.rpc('delete_estimate_cascade', {
        p_estimate_id: id
      });
      
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete estimate');
      }
      
      return result;
    },
    onSuccess: () => {
      notify.success('Estimate deleted successfully');
      navigate('/estimates');
    },
    onError: (error) => {
      console.error('Error deleting estimate:', error);
      notify.error(error);
    }
  });

  const cloneEstimate = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase.rpc('clone_estimate_with_updated_costs', {
        estimate_id: id,
      });

      if (error) throw error;
      
      notify.success('Estimate cloned with updated costs');
      if (data) {
        navigate(`/estimates/${data}`);
      }
    } catch (error) {
      console.error('Error cloning estimate:', error);
      notify.error(error);
    }
  };

  // Phase 2: Fix status display logic to use proper data source
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'sent':
        return <Badge variant="secondary">Sent</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-success text-success-foreground">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Fixed: Determine correct status to display based on viewing version
  const getDisplayStatus = () => {
    // If viewing a specific version, use its status
    if (viewingVersion?.status) {
      console.log('[EstimateViewPage] Using viewing version status:', viewingVersion.status, 'for version', viewingVersion.version_number);
      return viewingVersion.status;
    }
    // Fallback to estimate status if no versions exist
    if (estimate?.status) {
      console.log('[EstimateViewPage] Using estimate status fallback:', estimate.status);
      return estimate.status;
    }
    // Final fallback
    console.log('[EstimateViewPage] Using draft fallback');
    return 'draft';
  };

  const handleStatusChange = (newStatus: string) => {
    console.log('Status changed to:', newStatus);
    // Invalidate queries to refresh estimate and version data after status change
    queryClient.invalidateQueries({ queryKey: ['estimate', id] });
    queryClient.invalidateQueries({ queryKey: ['estimateVersions', id] });
    queryClient.invalidateQueries({ queryKey: ['estimateProject', estimate?.id] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
  };

  const handleDeleteClick = () => {
    setDeleteDialog({
      isOpen: true,
      estimateName: estimate?.title || 'Unknown'
    });
  };

  const handleDeleteConfirm = () => {
    deleteEstimateMutation.mutate();
    setDeleteDialog({ isOpen: false, estimateName: '' });
  };

  const isExpired = estimate?.expires_at && new Date(estimate.expires_at) < new Date();

  const createInvoice = () => {
    if (!id) return;
    navigate(`/invoices/create?estimate_id=${id}`);
  };

  const handleCustomerClick = () => {
    if (estimate?.customer_id) {
      navigate(`/customers?customer=${estimate.customer_id}`);
    }
  };

  const handleProjectClick = () => {
    if (linkedProject) {
      navigate(`/projects/${linkedProject.id}`);
    }
  };

  // Open send dialog with specific version - replaces the old status-only update
  const handleSendVersion = (versionId: string) => {
    setSendVersionId(versionId);
    setShowSendDialog(true);
  };

  // Clear sendVersionId when dialog closes
  const handleSendDialogChange = (open: boolean) => {
    setShowSendDialog(open);
    if (!open) {
      setSendVersionId(null);
    }
  };

  const handleDeleteVersionClick = (versionId: string, versionType: 'estimate' | 'change_order', versionNumber: number, versionStatus: string) => {
    setVersionDeleteDialog({
      isOpen: true,
      versionId,
      versionType,
      versionNumber,
      versionStatus
    });
  };

  const handleDeleteVersionConfirm = async () => {
    try {
      const { versionId, versionStatus } = versionDeleteDialog;
      
      if (versionStatus === 'approved') {
        const { error } = await supabase
          .from('estimate_versions')
          .update({ 
            status: 'cancelled',
            cancelled_date: new Date().toISOString()
          })
          .eq('id', versionId);

        if (error) throw error;
        notify.success('Version cancelled successfully');
      } else {
        const { error } = await supabase
          .from('estimate_versions')
          .delete()
          .eq('id', versionId);

        if (error) throw error;
        notify.success('Version deleted successfully');
      }
      
      queryClient.invalidateQueries({ queryKey: ['estimateVersions', id] });
      setCurrentViewVersion(null);
      setVersionDeleteDialog({
        isOpen: false,
        versionId: '',
        versionType: 'estimate',
        versionNumber: 0,
        versionStatus: 'draft'
      });
    } catch (error) {
      console.error('Error deleting/cancelling version:', error);
      notify.error(error);
    }
  };

  const handleApproveVersion = async (versionId: string) => {
    try {
      const { error } = await supabase
        .from('estimate_versions')
        .update({ 
          status: 'approved',
          accepted_date: new Date().toISOString()
        })
        .eq('id', versionId);

      if (error) throw error;
      
      notify.success('Version approved successfully');
      
      queryClient.invalidateQueries({ queryKey: ['estimateVersions', id] });
      queryClient.invalidateQueries({ queryKey: ['estimate', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    } catch (error) {
      console.error('Error approving version:', error);
      notify.error(error);
    }
  };

  const handleViewVersion = (versionId: string) => {
    setCurrentViewVersion(versionId);
    setShowVersionChanges(true);
  };

  const handleReturnToMain = () => {
    setCurrentViewVersion(null);
    setShowVersionChanges(false);
  };

  // Get status configuration for header badge
  const getStatusConfig = () => {
    const status = getDisplayStatus();
    const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" }> = {
      draft: { label: 'Draft', variant: 'outline' },
      sent: { label: 'Sent', variant: 'secondary' },
      approved: { label: 'Approved', variant: 'success' },
      rejected: { label: 'Rejected', variant: 'destructive' },
      expired: { label: 'Expired', variant: 'destructive' },
      cancelled: { label: 'Cancelled', variant: 'destructive' },
    };
    return statusMap[status] || { label: status, variant: 'outline' };
  };

  if (isLoadingEstimate) {
    return (
      <DetailPageLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64 lg:col-span-2" />
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  if (estimateError) {
    return (
      <DetailPageLayout>
        <div className="text-center py-12">
          <p className="text-destructive font-semibold mb-4">
            Error loading estimate: {estimateError instanceof Error ? estimateError.message : String(estimateError)}
          </p>
          <Link to="/estimates" className="text-primary hover:underline">
            Back to Estimates
          </Link>
        </div>
      </DetailPageLayout>
    );
  }

  if (!estimate) {
    return (
      <DetailPageLayout>
        <div className="text-center py-12">
          <p className="text-destructive font-semibold mb-4">
            Estimate not found for ID: {id}
          </p>
          <Link to="/estimates" className="text-primary hover:underline">
            Back to Estimates
          </Link>
        </div>
      </DetailPageLayout>
    );
  }

  // Build secondary actions for the header
  const headerSecondaryActions = [
    {
      label: "Preview",
      icon: Eye,
      onClick: () => setShowPreview(true)
    },
    {
      label: "Send to Customer",
      icon: Send,
      onClick: () => {
        const versionToSend = currentViewVersion || estimate?.main_version_id || null;
        setSendVersionId(versionToSend);
        setShowSendDialog(true);
      }
    },
    {
      label: "Cost Analysis",
      icon: Calculator,
      onClick: () => setShowCostAnalysis(true)
    },
    {
      label: "Clone with Current Costs",
      icon: Copy,
      onClick: cloneEstimate
    },
    ...(estimate.status === 'approved' ? [{
      label: "Create Invoice",
      icon: Receipt,
      onClick: createInvoice
    }] : []),
    {
      label: "Delete",
      icon: Trash2,
      onClick: handleDeleteClick,
      destructive: true,
      requiresConfirmation: {
        title: "Delete Estimate",
        description: `Are you sure you want to delete "${estimate.title}"? This action cannot be undone.`
      }
    }
  ];

  return (
    <DetailPageLayout>
      <DetailPageHeader
        title={estimate.estimate_number || estimate.title}
        subtitle={`Date: ${format(new Date((estimate as any).estimate_date || estimate.created_at), 'PPP')}`}
        status={getStatusConfig()}
        backLink={{ label: "Back to Estimates", href: "/estimates" }}
        primaryAction={{
          label: "Edit",
          icon: Edit,
          onClick: () => navigate(`/estimates/edit/${id}`)
        }}
        secondaryActions={headerSecondaryActions}
      />

      {/* Draft Version Banner */}
      {draftVersions.length > 0 && !currentViewVersion && (
        <DraftVersionBanner
          draftVersions={draftVersions.map(v => ({
            id: v.id,
            version_number: v.version_number,
            version_type: v.version_type as 'estimate' | 'change_order',
            created_at: v.created_at,
            reason_for_change: v.reason_for_change,
            change_description: v.change_description,
            total_amount: adjustedDraftTotals.get(v.id) ?? v.total_amount
          }))}
          onSendVersion={handleSendVersion}
          onDeleteVersion={(versionId: string) => {
            const version = draftVersions.find(v => v.id === versionId);
            if (version) {
              handleDeleteVersionClick(versionId, version.version_type as 'estimate' | 'change_order', version.version_number, 'draft');
            }
          }}
          onViewVersion={handleViewVersion}
        />
      )}

      {/* Version viewing header */}
      {currentViewVersion && viewingVersion && viewingVersion.id !== mainVersion?.id && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4 px-4 sm:py-6 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Eye className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-medium text-primary truncate">
                    Viewing {viewingVersion.version_type === 'change_order' ? 'Change Order' : 'Version'} v{viewingVersion.version_number}
                  </h3>
                  <p className="text-sm text-primary/80 truncate">
                    Status: {viewingVersion.status} • Created {format(new Date(viewingVersion.created_at), 'dd MMMM yyyy')}
                  </p>
                </div>
              </div>
              <ActionBar className="flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVersionChanges(!showVersionChanges)}
                >
                  {showVersionChanges ? 'Hide' : 'Show'} Changes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReturnToMain}
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Return to Main
                </Button>
              </ActionBar>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Version Changes Summary */}
      {showVersionChanges && viewingVersion && mainVersion && viewingVersion.id !== mainVersion.id && (
        <VersionSummary
          baseVersionData={mainVersion.line_items_snapshot}
          compareVersionData={viewingVersion.line_items_snapshot}
          baseVersionNumber={mainVersion.version_number}
          compareVersionNumber={viewingVersion.version_number}
          versionType={viewingVersion.version_type as 'estimate' | 'change_order'}
        />
      )}

      {/* Version Management Controls */}
      <ActionBar>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              Version {viewingVersion?.version_number || estimate.version}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80">
            {estimateVersions && estimateVersions.map((v) => (
              <div key={v.id} className="border-b last:border-b-0">
                <DropdownMenuItem 
                  onClick={() => handleViewVersion(v.id)}
                  className="p-3"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {v.version_type === 'change_order' ? 'Change Order' : 'Version'} {v.version_number}
                        </span>
                        {getStatusBadge(v.status)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(v.created_at), 'dd MMMM yyyy')} • R{v.total_amount.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                       {(v.id === mainVersion?.id) && (
                          <div title="Main Version" className="flex items-center">
                            <Crown className="h-4 w-4 text-warning" />
                          </div>
                        )}
                      {v.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVersionClick(v.id, v.version_type as 'estimate' | 'change_order', v.version_number, v.status);
                          }}
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          title={v.status === 'approved' ? "Cancel Version" : "Delete Version"}
                        >
                          {v.status === 'approved' ? <X className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
                        </Button>
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <VersionStatusManager
          versionId={viewingVersion?.id || estimate?.main_version_id || ''}
          currentStatus={(viewingVersion?.status || mainVersion?.status || 'draft') as any}
          versionType={(viewingVersion?.version_type || 'estimate') as 'estimate' | 'change_order'}
          versionNumber={viewingVersion?.version_number || mainVersion?.version_number || 1}
          estimateId={id}
          onStatusChange={handleStatusChange}
        />

        <EstimatePricingRefreshButton
          estimateId={id!}
          versionId={currentViewVersion || undefined}
          versionStatus={currentViewVersion && viewingVersion ? viewingVersion.status : estimate.status}
          versionType={currentViewVersion && viewingVersion ? viewingVersion.version_type : undefined}
          companyId={estimate.company_id}
          onVersionCreated={(versionId) => {
            queryClient.invalidateQueries({ queryKey: ['estimateVersions', id] });
            queryClient.invalidateQueries({ queryKey: ['estimate', id] });
            setCurrentViewVersion(versionId);
          }}
        />
      </ActionBar>

      {/* Show existing invoices if any */}
      {invoices && invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Invoices for this Estimate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border rounded-lg">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="font-mono font-medium">{invoice.invoice_number}</span>
                    <Badge variant={invoice.status === 'paid' ? 'default' : 'outline'}>
                      {invoice.status}
                    </Badge>
                    <span className="text-sm text-muted-foreground hidden sm:inline">
                      {format(new Date(invoice.created_at), 'dd MMMM yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2">
                    <span className="font-medium whitespace-nowrap font-mono">{formatCurrency(invoice.total_amount || 0)}</span>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/invoices/${invoice.id}`}>
                        View
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Estimate Details */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Estimate Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold">{estimate.title}</h2>
                <div className="flex items-center gap-2 mt-1">
                  {/* Phase 2: Use the new getDisplayStatus function for correct status display */}
                  {getStatusBadge(getDisplayStatus())}
                  <span className="text-sm text-muted-foreground">
                    Version {viewingVersion?.version_number || mainVersion?.version_number || estimate.version}
                  </span>
                  {isExpired && (
                    <Badge variant="destructive">Expired</Badge>
                  )}
                </div>
              </div>

              {estimate.estimate_number && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Estimate Number</h3>
                  <p className="font-mono">{estimate.estimate_number}</p>
                </div>
              )}
              
              {estimate.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
                  <p>{estimate.description}</p>
                </div>
              )}
              
              {estimate.customers && (
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{getCustomerDisplayName(estimate.customers)}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCustomerClick}
                      className="w-full sm:w-auto"
                    >
                      View Customer
                    </Button>
                  </div>
                </div>
              )}

              {linkedProject && (
                <div className="p-4 bg-success/5 rounded-lg border border-success/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="h-4 w-4 text-success flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium truncate block">{linkedProject.name}</span>
                        {linkedProject.status && (
                          <span className="text-xs text-muted-foreground">Status: {linkedProject.status}</span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleProjectClick}
                      className="w-full sm:w-auto"
                    >
                      View Project
                    </Button>
                  </div>
                </div>
              )}
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Estimate Date</h3>
                <Popover open={estimateDateOpen} onOpenChange={setEstimateDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !(estimate as any).estimate_date && !estimate.created_at && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(new Date((estimate as any).estimate_date || estimate.created_at), 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date((estimate as any).estimate_date || estimate.created_at)}
                      onSelect={async (date) => {
                        if (date) {
                          const { error } = await supabase
                            .from('estimates')
                            .update({ estimate_date: date.toISOString() } as any)
                            .eq('id', id!);
                          if (error) {
                            console.error('Error updating estimate date:', error);
                            notify.error('Failed to update estimate date');
                          } else {
                            notify.success('Estimate date updated');
                            queryClient.invalidateQueries({ queryKey: ['estimate', id] });
                          }
                          setEstimateDateOpen(false);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Show version date when viewing a specific version */}
              {currentViewVersion && viewingVersion && viewingVersion.id !== mainVersion?.id && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Version Date</h3>
                  <p>{format(new Date(viewingVersion.created_at), 'PPP')}</p>
                </div>
              )}

              {estimate.expires_at && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Expires</h3>
                  <p className={isExpired ? 'text-red-600 font-medium' : ''}>
                    {format(new Date(estimate.expires_at), 'PPP')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-primary">
                  <span>Discount (-{discountPercentage}%):</span>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              {company?.is_vat_registered && (company?.["VAT Rate"] || 0) > 0 && (
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>VAT ({company?.["VAT Rate"] || 15}%):</span>
                  <span>{formatCurrency(taxAmount)}</span>
                </div>
              )}
              <div className="border-t pt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span>{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          {(estimate.payment_terms || estimate.special_instructions) && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {estimate.payment_terms && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Payment Terms</h3>
                    <p className="text-sm">{estimate.payment_terms}</p>
                  </div>
                )}
                {estimate.special_instructions && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Special Instructions</h3>
                    <p className="text-sm">{estimate.special_instructions}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Email History */}
          <DocumentSendHistory 
            documentType="estimate" 
            documentId={id!}
            customerId={estimate?.customer_id}
          />
        </div>

        {/* Right Column - Line Items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Line Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            <DataTableWrapper
              isLoading={isLoadingLineItems}
              isEmpty={!displayLineItems || displayLineItems.length === 0}
              emptyState={{
                title: "No line items",
                description: "No line items found in this estimate."
              }}
              skeletonRows={5}
              skeletonColumns={5}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="hidden sm:table-cell">Unit</TableHead>
                    <TableHead className="hidden sm:table-cell">Qty</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Unit Cost</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayLineItems?.map((item: any, index: number) => (
                    <TableRow key={item.id || index}>
                      <TableCell className="min-w-0">
                        <div className="truncate max-w-[200px] sm:max-w-none">
                          {getDisplayDescription(item)}
                        </div>
                        {/* Show unit and qty on mobile as subtext */}
                        <div className="text-xs text-muted-foreground sm:hidden">
                          {item.quantity_in_base_units || item.quantity} {getUnitLabel(item.base_unit)}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{getUnitLabel(item.base_unit)}</TableCell>
                      <TableCell className="hidden sm:table-cell">{item.quantity_in_base_units || item.quantity}</TableCell>
                      <TableCell className={`hidden md:table-cell text-right ${item.manual_override ? 'text-primary font-medium' : ''}`}>
                        {formatCurrency(getFinalLineItemUnitCost(item)).replace('R', '')}
                      </TableCell>
                      <TableCell className={`text-right ${item.manual_override ? 'text-primary font-medium' : ''}`}>
                        {formatCurrency(getFinalLineItemAmount(item))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DataTableWrapper>
          </CardContent>
        </Card>
      </div>

      {/* Preview Modal */}
      {id && (
        <EstimatePreviewModal
          estimateId={id}
          versionId={viewingVersion?.id}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Cost Analysis Modal */}
      {id && (
        <CostAnalysisModal
          estimateId={id}
          isOpen={showCostAnalysis}
          onClose={() => setShowCostAnalysis(false)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, estimateName: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Estimate"
        description={
          estimate?.status === 'approved'
            ? "This estimate is approved and has related records that will also be permanently deleted: Associated project data, estimate versions and change orders, related invoices (if any), and all line items and calculations. This action cannot be undone. Are you sure you want to delete the estimate"
            : "Are you sure you want to delete the estimate"
        }
        itemName={deleteDialog.estimateName}
      />

      {/* Version Deletion Dialog */}
      <VersionDeletionDialog
        isOpen={versionDeleteDialog.isOpen}
        onOpenChange={(open) => setVersionDeleteDialog(prev => ({ ...prev, isOpen: open }))}
        onConfirm={handleDeleteVersionConfirm}
        versionType={versionDeleteDialog.versionType}
        versionNumber={versionDeleteDialog.versionNumber}
        versionStatus={versionDeleteDialog.versionStatus}
      />

      {/* Send Estimate Dialog */}
      <SendEstimateDialog
        open={showSendDialog}
        onOpenChange={handleSendDialogChange}
        estimateId={id!}
        estimateNumber={estimate?.estimate_number}
        customerId={estimate?.customer_id}
        companyId={estimate?.company_id}
        siteAddress={estimate?.use_separate_site_address 
          ? [estimate.site_street_address, estimate.site_suburb, estimate.site_city].filter(Boolean).join(', ')
          : undefined
        }
        versionId={sendVersionId || undefined}
      />
    </DetailPageLayout>
  );
};

export default EstimateViewPage;
